"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createTransaction = exports.isTransactionPaid = exports.createTransactionBody = exports.createReceiptNumber = exports.createPaymentEntry = exports.getPaymentReference = void 0;
const carts_1 = require("./carts");
/**
 * Create a reference id to be used in payments
 * @param  {number | string} storeId - Numeric number that represent store
 * @param  {number | string} deviceId - Numeric number that represent device
 * @returns {string} Unique reference id with format X-X-1231312
 */
const getPaymentReference = (storeId, deviceId) => {
    return `${storeId || '0'}-${deviceId || '0'}-${Math.round(+new Date())}`;
};
exports.getPaymentReference = getPaymentReference;
/**
 * Create a payment entry to be pushed into payments array of a cart/transaction
 * @param  {number} paidAmount - Value to paid
 * @param  {string} method - Name displayed as payment method
 * @param  {string} provider - Provider set on extra payment method (null or  '' if is just to group)
 * @param  {any} info? - All payment data that came from payment provider
 * @param  {string} ticket? - Printed ticket that payment terminal print to client
 * @param  {number} tip? - Amount of the tip (usually paid - total)
 * @returns {PaymentEntry} Returs an obj that we push to payments array to complete transaction
 */
const createPaymentEntry = (paidAmount, method, provider, info, ticket, tip) => {
    const payment = {
        date: new Date(),
        payment_started: new Date(),
        paid: paidAmount,
        method,
        provider,
        info: info !== null && info !== void 0 ? info : {},
        card_print_info: ticket !== null && ticket !== void 0 ? ticket : '',
        tip: tip !== null && tip !== void 0 ? tip : 0,
        postponed: false
    };
    return payment;
};
exports.createPaymentEntry = createPaymentEntry;
/**
 * Return receipt number obj (receipt_id and receipt_number) from the last receipt number used
 * @param  {number} lastTransaction - last receipt number used in the last transaction
 * @param  {number} store_id - int that identify the store
 * @param  {number} device_id - int that identify the device
 * @returns {ReceiptNumber} Return the obj with next receipt id and number
 */
const createReceiptNumber = (lastTransaction, store_id, device_id) => {
    let numberString = (lastTransaction + 1).toString();
    for (let i = numberString.length; i < 6; i++) {
        numberString = '0' + numberString;
    }
    return {
        receipt_id: `${store_id}-${device_id}-${numberString}`,
        receipt_number: parseInt(numberString, 10)
    };
};
exports.createReceiptNumber = createReceiptNumber;
/**
 * Create the basic info needed to create a transaction
 * @param  {Cart} cart - Cart obj
 * @param  {Device} device - Device obj
 * @param  {PaymentEntry} payment - Payment obj
 * @param  {ReceiptNumber} receipt - Next receipt number that will be add to this transaction
 * @param  {string} orderSource - POS, WEB_POS, WAITER, KIOSK
 * @returns {TransactionBody} Basic object that is need to create the transaction
 */
const createTransactionBody = (cart, device, payment, receipt, orderSource) => {
    const body = {
        store: cart.store,
        device: device._id,
        date: new Date().toISOString(),
        timezone: cart.timezone,
        currency: cart.currency,
        customer: cart.customer,
        receipt_id: receipt.receipt_id,
        receipt_number: receipt.receipt_number,
        order_source: orderSource,
        employee: cart.employee,
        items: cart.items,
        payments: [payment],
        paid: cart.paid,
        total: cart.total,
        sub_total: cart.sub_total,
        discount: cart.discount,
        change: (0, carts_1.calculateCartChange)(cart, payment.paid),
        extras: cart.extras,
        reduction: cart.reduction
    };
    // Adding new payment to payments array and recalculating paid value
    body.paid = body.payments.reduce((acc, current) => acc + (!current.postponed ? current.paid : 0), 0);
    return body;
};
exports.createTransactionBody = createTransactionBody;
/**
 * Checks if transaction body paid value is bigger or equal than body's total
 * @param  {TransactionBody} transactionBody - transaction obj
 * @returns {boolean} Returns true if paid is bigger or equal than total
 */
const isTransactionPaid = (transactionBody) => {
    const { total } = transactionBody;
    const paid = transactionBody.payments.reduce((acc, payment) => acc + payment.paid, 0);
    const paidCents = Math.round(paid * 100);
    const totalCents = Math.round(total * 100);
    return paidCents >= totalCents;
};
exports.isTransactionPaid = isTransactionPaid;
/**
 * Wrapper of CountrSDK transaction create method
 * @param  {any} sdk - Countr SDK
 * @param  {TransactionBody} transactionBody - Basic object that is need to create the transaction
 * @returns {Promise<Transaction>} Returns promise with create transaction or error
 */
const createTransaction = (sdk, transactionBody) => {
    if (!(0, exports.isTransactionPaid)(transactionBody)) {
        return Promise.reject({
            status: 'fail',
            message: "Transaction paid isn't bigger or equal to transaction total",
            extra: {
                paid: transactionBody.paid,
                total: transactionBody.total
            }
        });
    }
    return sdk.transactions.create(transactionBody).then((transaction) => transaction, (error) => error);
};
exports.createTransaction = createTransaction;
